import React, { useState } from 'react';
import { X, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ToolFeedback - Post-tool "How did that feel?" feedback modal
 * 
 * Shows a quick mood check after completing a tool to:
 * - Help users recognize what works for them
 * - Feed data to AI Guide for better recommendations
 * - Track progress over time
 */

const MOOD_OPTIONS = [
    { id: 'much-worse', emoji: '😫', label: 'Much worse', value: -2 },
    { id: 'worse', emoji: '😕', label: 'A bit worse', value: -1 },
    { id: 'same', emoji: '😐', label: 'About the same', value: 0 },
    { id: 'better', emoji: '🙂', label: 'A bit better', value: 1 },
    { id: 'much-better', emoji: '😌', label: 'Much better', value: 2 }
];

export const ToolFeedback = ({ 
    isOpen, 
    onClose, 
    toolId, 
    toolName,
    onFeedbackSubmit 
}) => {
    const [selectedMood, setSelectedMood] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showThankYou, setShowThankYou] = useState(false);

    const handleMoodSelect = async (mood) => {
        setSelectedMood(mood.id);
        setIsSubmitting(true);

        try {
            const token = localStorage.getItem('co_regulate_token');
            
            // Save feedback to backend
            await fetch(`${API_URL}/api/tool-feedback`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { 'Authorization': `Bearer ${token}` })
                },
                body: JSON.stringify({
                    tool_id: toolId,
                    tool_name: toolName,
                    mood_rating: mood.value,
                    mood_label: mood.label,
                    timestamp: new Date().toISOString()
                })
            });

            // Also submit to promo feedback system (converts mood to 1-5 star rating)
            // Mood values: -2, -1, 0, 1, 2 -> Star ratings: 1, 2, 3, 4, 5
            const starRating = mood.value + 3; // Convert -2..2 to 1..5
            if (token) {
                await fetch(`${API_URL}/api/feedback/tool`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        tool_name: toolName,
                        rating: starRating,
                        comment: null,
                        session_context: mood.label
                    })
                }).catch(() => {}); // Silently fail if promo feedback endpoint doesn't exist
            }

            // Also store locally for quick access
            const feedbackHistory = JSON.parse(localStorage.getItem('coregulate_tool_feedback') || '[]');
            feedbackHistory.push({
                tool_id: toolId,
                tool_name: toolName,
                mood_rating: mood.value,
                mood_label: mood.label,
                timestamp: new Date().toISOString()
            });
            // Keep last 50 entries
            if (feedbackHistory.length > 50) {
                feedbackHistory.shift();
            }
            localStorage.setItem('coregulate_tool_feedback', JSON.stringify(feedbackHistory));

        } catch (error) {
            console.error('Failed to save feedback:', error);
            // Still continue - localStorage is the fallback
        }

        setIsSubmitting(false);
        setShowThankYou(true);

        // Auto-close after showing thank you
        setTimeout(() => {
            onFeedbackSubmit?.(mood);
            onClose();
            // Reset state for next time
            setSelectedMood(null);
            setShowThankYou(false);
        }, 1500);
    };

    const handleSkip = () => {
        onClose();
        setSelectedMood(null);
        setShowThankYou(false);
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm"
                onClick={handleSkip}
            >
                <motion.div
                    initial={{ scale: 0.9, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.9, opacity: 0, y: 20 }}
                    transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                    className="glass-card-elevated w-full max-w-sm p-6 rounded-3xl"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="tool-feedback-modal"
                >
                    {!showThankYou ? (
                        <>
                            {/* Header */}
                            <div className="flex justify-between items-start mb-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-[#1D6B6B] mb-1">
                                        How did that feel?
                                    </h3>
                                    <p className="text-sm text-[#7D8E95]">
                                        Your feedback helps personalize your experience
                                    </p>
                                </div>
                                <button 
                                    onClick={handleSkip}
                                    className="p-1.5 rounded-lg hover:bg-[#7D8E95]/10 transition-colors"
                                    data-testid="feedback-close-btn"
                                >
                                    <X className="w-5 h-5 text-[#7D8E95]" />
                                </button>
                            </div>

                            {/* Mood Options */}
                            <div className="flex justify-between gap-2 mb-6">
                                {MOOD_OPTIONS.map((mood) => (
                                    <button
                                        key={mood.id}
                                        onClick={() => handleMoodSelect(mood)}
                                        disabled={isSubmitting}
                                        className={`flex-1 flex flex-col items-center p-3 rounded-xl transition-all duration-200 
                                            ${selectedMood === mood.id 
                                                ? 'bg-[#FFBB98]/30 scale-105 ring-2 ring-[#FFBB98]' 
                                                : 'hover:bg-[#7D8E95]/10 hover:scale-105'
                                            }
                                            ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                        `}
                                        data-testid={`feedback-mood-${mood.id}`}
                                    >
                                        <span className="text-2xl mb-1">{mood.emoji}</span>
                                        <span className="text-xs text-[#7D8E95] text-center leading-tight">
                                            {mood.label}
                                        </span>
                                    </button>
                                ))}
                            </div>

                            {/* Skip Option */}
                            <button 
                                onClick={handleSkip}
                                className="w-full text-center text-sm text-[#7D8E95] hover:text-[#1D6B6B] transition-colors"
                                data-testid="feedback-skip-btn"
                            >
                                Skip for now
                            </button>
                        </>
                    ) : (
                        /* Thank You State */
                        <motion.div 
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="text-center py-4"
                        >
                            <div className="w-16 h-16 rounded-full bg-[#FFBB98]/20 flex items-center justify-center mx-auto mb-4">
                                <Sparkles className="w-8 h-8 text-[#FFBB98]" />
                            </div>
                            <h3 className="text-lg font-semibold text-[#1D6B6B] mb-1">
                                Thanks for sharing!
                            </h3>
                            <p className="text-sm text-[#7D8E95]">
                                This helps us recommend better tools for you
                            </p>
                        </motion.div>
                    )}
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

/**
 * Hook for managing tool feedback
 * Use this in any tool page to show feedback after completion
 */
export const useToolFeedback = () => {
    const [showFeedback, setShowFeedback] = useState(false);
    const [currentTool, setCurrentTool] = useState({ id: '', name: '' });

    const triggerFeedback = (toolId, toolName) => {
        setCurrentTool({ id: toolId, name: toolName });
        setShowFeedback(true);
    };

    const closeFeedback = () => {
        setShowFeedback(false);
    };

    return {
        showFeedback,
        currentTool,
        triggerFeedback,
        closeFeedback,
        FeedbackModal: () => (
            <ToolFeedback
                isOpen={showFeedback}
                onClose={closeFeedback}
                toolId={currentTool.id}
                toolName={currentTool.name}
            />
        )
    };
};

/**
 * Get recent feedback for a specific tool
 */
export const getToolFeedbackHistory = (toolId = null) => {
    try {
        const history = JSON.parse(localStorage.getItem('coregulate_tool_feedback') || '[]');
        if (toolId) {
            return history.filter(f => f.tool_id === toolId);
        }
        return history;
    } catch {
        return [];
    }
};

/**
 * Get average mood rating for a tool
 */
export const getToolAverageRating = (toolId) => {
    const history = getToolFeedbackHistory(toolId);
    if (history.length === 0) return null;
    
    const sum = history.reduce((acc, f) => acc + f.mood_rating, 0);
    return sum / history.length;
};

export default ToolFeedback;
